import React, { useState, useMemo } from 'react';
import { PlusIcon, ArchiveBoxIcon, MagnifyingGlassIcon, DocumentTextIcon, TagIcon, FunnelIcon, UserIcon } from '@heroicons/react/24/outline';
import StickyNote from '../components/notes/StickyNote';
import { useNotes } from '../hooks/useNotes';
import { useCustomers } from '../hooks/useCustomers';
import { useLanguage } from '../contexts/LanguageContext';
import LoadingScreen from '../components/common/LoadingScreen';
import CommonPageHeader from '../components/common/CommonPageHeader';

const Notes: React.FC = () => {
  const { t } = useLanguage();
  const { notes, loading, createNote, updateNote, deleteNote, updateNotePosition } = useNotes();
  const { customers } = useCustomers();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState<string>('');
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');
  const [groupBy, setGroupBy] = useState<'none' | 'tag' | 'color' | 'date'>('none');

  // Új jegyzet létrehozása véletlenszerű pozícióval
  const handleCreateNote = async () => {
    const colors = ['yellow', 'blue', 'green', 'pink', 'purple', 'orange'] as const;
    const randomColor = colors[Math.floor(Math.random() * colors.length)];
    
    // Véletlenszerű pozíció a teljes canvas területen
    // Figyelembe vesszük a header magasságát és a note méretét
    const canvasWidth = window.innerWidth - 32; // padding miatt
    const canvasHeight = window.innerHeight - 200; // header + padding miatt
    
    const maxX = Math.max(50, canvasWidth - 280); // note width: 256px + margin
    const maxY = Math.max(50, canvasHeight - 280); // note height: 256px + margin
    
    const randomX = Math.floor(Math.random() * maxX) + 20;
    const randomY = Math.floor(Math.random() * maxY) + 20;

    await createNote({
      content: '',
      color: randomColor,
      position: { x: randomX, y: randomY }
    });
  };

  // Összes tag összegyűjtése
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    notes.forEach(note => {
      note.tags?.forEach(tag => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [notes]);

  // Keresés és szűrés a jegyzetekben
  const filteredNotes = useMemo(() => {
    return notes.filter(note => {
      // Szöveges keresés
      const matchesSearch = !searchTerm || 
        note.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (note.tags && note.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))) ||
        (note.customers && note.customers.some(c => c.name.toLowerCase().includes(searchTerm.toLowerCase())));
      
      // Tag szűrés
      const matchesTag = !selectedTag || (note.tags && note.tags.includes(selectedTag));
      
      // Ügyfél szűrés - több ügyfél támogatása
      const matchesCustomer = !selectedCustomer || (note.customerIds && note.customerIds.includes(selectedCustomer));
      
      return matchesSearch && matchesTag && matchesCustomer;
    });
  }, [notes, searchTerm, selectedTag, selectedCustomer]);

  // Csoportosítás
  const groupedNotes = useMemo(() => {
    if (groupBy === 'none') {
      return { 'Minden jegyzet': filteredNotes };
    }

    const groups: Record<string, typeof filteredNotes> = {};

    filteredNotes.forEach(note => {
      let groupKey = '';
      
      switch (groupBy) {
        case 'tag':
          if (note.tags && note.tags.length > 0) {
            note.tags.forEach(tag => {
              if (!groups[tag]) groups[tag] = [];
              groups[tag].push(note);
            });
          } else {
            const noTagLabel = t('notes.tags.none') || 'Tag nélkül';
            if (!groups[noTagLabel]) groups[noTagLabel] = [];
            groups[noTagLabel].push(note);
          }
          return;
        
        case 'color':
          groupKey = note.color;
          break;
        
        case 'date':
          const today = new Date();
          const noteDate = new Date(note.createdAt);
          const diffTime = today.getTime() - noteDate.getTime();
          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
          
          if (diffDays === 0) groupKey = 'Ma';
          else if (diffDays === 1) groupKey = 'Tegnap';
          else if (diffDays <= 7) groupKey = 'Ezen a héten';
          else if (diffDays <= 30) groupKey = 'Ezen a hónapban';
          else groupKey = 'Régebbi';
          break;
      }
      
      if (!groups[groupKey]) groups[groupKey] = [];
      groups[groupKey].push(note);
    });

    return groups;
  }, [filteredNotes, groupBy]);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="h-full flex flex-col">
      <CommonPageHeader
        title={t('notes.title') || 'Jegyzetek'}
        subtitle={t('notes.subtitle') || 'Sticky Notes - Gyors jegyzetelés'}
        icon={DocumentTextIcon}
      >
        <div className="flex items-center space-x-2">
          {/* Keresés */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('notes.search.placeholder') || 'Keresés a jegyzetekben...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-white/30 rounded-lg text-sm bg-white/20 backdrop-blur-sm text-white placeholder-gray-300 focus:ring-2 focus:ring-white/50 focus:border-transparent"
            />
          </div>

          {/* Tag szűrő */}
          {allTags.length > 0 && (
            <select
              value={selectedTag}
              onChange={(e) => setSelectedTag(e.target.value)}
              className="px-3 py-2 border border-white/30 rounded-lg text-sm bg-white/20 backdrop-blur-sm text-white focus:ring-2 focus:ring-white/50 focus:border-transparent"
            >
              <option value="">{t('notes.tags.all') || 'Minden tag'}</option>
              {allTags.map(tag => (
                <option key={tag} value={tag} className="text-gray-900">{tag}</option>
              ))}
            </select>
          )}

          {/* Ügyfél szűrő */}
          <div className="relative">
            <select
              value={selectedCustomer}
              onChange={(e) => setSelectedCustomer(e.target.value)}
              className="pl-8 pr-4 py-2 border border-white/30 rounded-lg text-sm bg-white/20 backdrop-blur-sm text-white focus:ring-2 focus:ring-white/50 focus:border-transparent appearance-none"
            >
              <option value="" className="text-gray-900">Minden ügyfél</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id} className="text-gray-900">
                  {customer.type === 'organization' ? customer.companyName : `${customer.lastName} ${customer.firstName}`}
                </option>
              ))}
            </select>
            <UserIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-300 pointer-events-none" />
          </div>

          {/* Csoportosítás */}
          <div className="relative">
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as typeof groupBy)}
              className="pl-8 pr-4 py-2 border border-white/30 rounded-lg text-sm bg-white/20 backdrop-blur-sm text-white focus:ring-2 focus:ring-white/50 focus:border-transparent appearance-none"
            >
              <option value="none" className="text-gray-900">{t('notes.grouping.none') || 'Csoportosítás nélkül'}</option>
              <option value="tag" className="text-gray-900">{t('notes.grouping.byTag') || 'Tag szerint'}</option>
              <option value="color" className="text-gray-900">{t('notes.grouping.byColor') || 'Szín szerint'}</option>
              <option value="date" className="text-gray-900">{t('notes.grouping.byDate') || 'Dátum szerint'}</option>
            </select>
            <FunnelIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-300 pointer-events-none" />
          </div>

          {/* Archív gomb */}
          <button className="flex items-center space-x-2 px-3 py-2 bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 rounded-lg transition-colors text-sm">
            <ArchiveBoxIcon className="h-4 w-4" />
            <span className="hidden sm:inline">{t('notes.menu.archive') || 'Archív'}</span>
          </button>

          {/* Új jegyzet gomb */}
          <button
            onClick={handleCreateNote}
            className="flex items-center space-x-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-lg hover:bg-white/30 transition-colors shadow-sm"
          >
            <PlusIcon className="h-4 w-4" />
            <span className="text-sm font-medium">{t('notes.actions.createNote') || 'Új jegyzet'}</span>
          </button>
        </div>
      </CommonPageHeader>

      {/* Jegyzetek terület - teljes szélességű */}
      <div className="flex-1 relative bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 overflow-hidden">
        {/* Háttér rács */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `
              linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '20px 20px'
          }}
        />

        {/* Jegyzetek */}
        <div className="relative w-full h-full">
          {Object.keys(groupedNotes).length === 0 || Object.values(groupedNotes).every(group => group.length === 0) ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
              <div className="text-6xl mb-4">📝</div>
              <h3 className="text-xl font-medium mb-2">
                {searchTerm || selectedTag
                  ? (t('notes.status.noResults') || 'Nincs találat')
                  : (t('notes.status.noNotes') || 'Még nincsenek jegyzeteid')
                }
              </h3>
              <p className="text-center max-w-md">
                {searchTerm || selectedTag
                  ? (t('notes.search.noResults') || `Nincs jegyzet a szűrési feltételekre`)
                  : (t('notes.tips.create') || 'Kattints az "Új jegyzet" gombra a első jegyzeted létrehozásához!')
                }
              </p>
              {!searchTerm && !selectedTag && (
                <button
                  onClick={handleCreateNote}
                  className="mt-6 flex items-center space-x-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg transition-colors shadow-sm"
                >
                  <PlusIcon className="h-5 w-5" />
                  <span>{t('notes.actions.createNote') || 'Első jegyzetem'}</span>
                </button>
              )}
            </div>
          ) : (
            <>
              {Object.entries(groupedNotes).map(([groupName, groupNotes]) => (
                <div key={groupName}>
                  {groupBy !== 'none' && (
                    <div className="absolute top-4 left-4 z-30">
                      <div className="bg-white dark:bg-gray-800 px-3 py-1 rounded-full shadow-sm border border-gray-200 dark:border-gray-600">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {groupName} ({groupNotes.length})
                        </span>
                      </div>
                    </div>
                  )}
                  {groupNotes.map((note) => (
                    <StickyNote
                      key={note.id}
                      note={note}
                      onUpdate={updateNote}
                      onDelete={deleteNote}
                      onPositionChange={updateNotePosition}
                    />
                  ))}
                </div>
              ))}
            </>
          )}
        </div>

        {/* Gyorsbillentyűk súgó */}
        <div className="absolute bottom-4 left-4 text-xs text-gray-400 dark:text-gray-500">
          <div>💡 {t('notes.tips.title') || 'Tippek'}:</div>
          <div>• {t('notes.tips.drag') || 'Húzd a jegyzeteket az áthelyezéshez'}</div>
          <div>• {t('notes.tips.edit') || 'Kattints a ceruza ikonra a szerkesztéshez'}</div>
          <div>• {t('notes.tips.menu') || 'Használd a három pont menüt a színváltáshoz'}</div>
        </div>
      </div>
    </div>
  );
};

export default Notes;
